import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useWebSocket from './useWebSocket';
import VideoComparison from './VideoComparison';
import StrokeAnalysis from './StrokeAnalysis';
import './App.css';

const API_BASE_URL = '/api';

// Debug function to check API connectivity
const checkApiConnectivity = async () => {
  try {
    console.log('Checking API connectivity...');
    const response = await axios.get('/health');
    console.log('API health check successful:', response.data);
    return true;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

function App() {
  const [file, setFile] = useState(null);
  const [configs, setConfigs] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState('default');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showVideoComparison, setShowVideoComparison] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('original'); // 'original' | 'reference'
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(null);
  
  // WebSocket connection - will be set after configs are loaded
  const [wsUrl, setWsUrl] = useState(null);
  const { isConnected, lastMessage, subscribeToJob, unsubscribeFromJob } = useWebSocket(wsUrl);

  // Define checkJobStatus function early to avoid hoisting issues
  const checkJobStatus = useCallback(async () => {
    if (!jobId) return;

    console.log('Manually checking job status for:', jobId);
    try {
      const response = await axios.get(`${API_BASE_URL}/status/${jobId}`);
      console.log('Job status update:', response.data);
      setJobStatus(response.data);
      
      // Stop loading when job is completed or has error
      if (response.data.status === 'completed' || response.data.status === 'error') {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check job status:', error);
      setError('Failed to check analysis status');
    }
  }, [jobId]);

  // Enhanced Configuration state
  const [preprocessingConfig, setPreprocessingConfig] = useState({
    enable_resize_to_720p: true,
    enable_contrast_enhancement: true,
    enable_noise_reduction: true,
    enable_color_correction: true,
    enable_visibility_enhancement: true,
    contrast_blend_alpha: 0.4,
    clahe_clip_limit: 2.5,
    clahe_tile_size: 8,
    noise_blend_alpha: 0.3,
    blur_strength: 0.6,
    noise_reduction_method: 'gaussian',
    bilateral_d: 9,
    bilateral_sigmaColor: 75,
    bilateral_sigmaSpace: 75,
    color_blend_alpha: 0.3,
    blue_factor: 0.93,
    red_factor: 1.07,
    visibility_blend_alpha: 0.3,
    saturation_factor: 1.15,
    brightness_offset: 15
  });

  const [smoothingConfig, setSmoothingConfig] = useState({
    method: 'moving_average',
    window_size: 3,
    exponential_alpha: 0.4,
    savgol_window_length: 5,
    savgol_polyorder: 2,
    kalman_process_noise: 1e-3,
    kalman_measurement_noise: 1e-1
  });

  const [poseConfig, setPoseConfig] = useState({
    min_detection_confidence: 0.1,
    min_tracking_confidence: 0.1,
    model_complexity: 1
  });

  const [performanceConfig, setPerformanceConfig] = useState({
    profile: 'optimized_detection',
    enable_gpu: true,
    enable_parallel: true,
    batch_size: 4
  });

  const [swimType, setSwimType] = useState('workout');

  useEffect(() => {
    const initializeApp = async () => {
      console.log('Initializing SwimSense web app...');
      
      // Check API connectivity first
      const isApiConnected = await checkApiConnectivity();
      if (!isApiConnected) {
        setError('Cannot connect to SwimSense API. Please ensure the API is running on port 8000.');
        return;
      }
      
      // Load configurations
      await loadConfigurations();
    };
    
    initializeApp();
  }, []);

  useEffect(() => {
    if (jobId && isConnected) {
      console.log('Subscribing to WebSocket updates for job:', jobId);
      subscribeToJob(jobId);
      
      return () => {
        console.log('Unsubscribing from WebSocket updates for job:', jobId);
        unsubscribeFromJob(jobId);
      };
    }
  }, [jobId, isConnected, subscribeToJob, unsubscribeFromJob]);

          // Handle WebSocket messages for job updates and upload progress
        useEffect(() => {
          if (lastMessage) {
            try {
              const data = lastMessage; // lastMessage is already parsed by useWebSocket hook
              console.log('WebSocket message received:', data);
        
        // Handle upload progress messages
        if (data.type === 'upload_start') {
          setUploadProgress({
            type: 'upload_start',
            uploadId: data.uploadId,
            fileName: data.fileName,
            totalSize: data.totalSize,
            message: data.message,
            progress: 0
          });
        } else if (data.type === 'upload_progress') {
          setUploadProgress({
            type: 'upload_progress',
            uploadId: data.uploadId,
            uploadedBytes: data.uploadedBytes,
            totalSize: data.totalSize,
            progress: data.progress,
            message: data.message
          });
        } else if (data.type === 'upload_complete') {
          setUploadProgress({
            type: 'upload_complete',
            uploadId: data.uploadId,
            uploadedBytes: data.uploadedBytes,
            totalSize: data.totalSize,
            progress: 100,
            message: data.message
          });
        } else if (data.type === 'upload_processing') {
          setUploadProgress({
            type: 'upload_processing',
            uploadId: data.uploadId,
            message: data.message
          });
        } else if (data.type === 'analysis_started') {
          setUploadProgress({
            type: 'analysis_started',
            uploadId: data.uploadId,
            jobId: data.jobId,
            message: data.message
          });
          // Clear upload progress and start job tracking
          setUploadProgress(null);
          if (data.jobId) {
            setJobId(data.jobId);
            subscribeToJob(data.jobId);
          }
        } else if (data.type === 'upload_error') {
          setUploadProgress({
            type: 'upload_error',
            uploadId: data.uploadId,
            error: data.error,
            details: data.details
          });
          setError(data.error);
        }
        // Handle job update messages (existing functionality)
        else if (data.type === 'job_update' && data.job_id) {
          console.log('Job update received:', data.data);
          setJobStatus(data.data);
          
          // Stop loading when job is completed or has error
          if (data.data.status === 'completed' || data.data.status === 'error') {
            setLoading(false);
            unsubscribeFromJob(data.job_id);
          }
        }
                  } catch (error) {
              console.error('Error handling WebSocket message:', error);
            }
    }
  }, [lastMessage, subscribeToJob, unsubscribeFromJob]);

  // Fallback polling when WebSocket is not connected
  useEffect(() => {
    if (jobId && !isConnected && jobStatus && jobStatus.status === 'processing') {
      console.log('WebSocket not connected, setting up fallback polling');
      const interval = setInterval(() => {
        checkJobStatus();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [jobId, isConnected, jobStatus, checkJobStatus]);

  // Scroll to status section when jobStatus is first set (analysis starts)
  useEffect(() => {
    if (jobStatus && !loading) {
      const statusSection = document.getElementById('analysis-status');
      if (statusSection) {
        statusSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [jobStatus, loading]);

  // Scroll to results section when analysis completes
  useEffect(() => {
    if (jobStatus && jobStatus.status === 'completed') {
      const resultsSection = document.getElementById('analysis-results');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [jobStatus]);

  const loadConfigurations = async () => {
    try {
      console.log('Loading configurations from API...');
      const response = await axios.get(`${API_BASE_URL}/configs`);
      console.log('Configurations loaded:', response.data);
      setConfigs(response.data);
      
      // Set WebSocket URL from server configuration
      if (response.data.wsUrl) {
        console.log('Setting WebSocket URL from server config:', response.data.wsUrl);
        setWsUrl(response.data.wsUrl);
      } else {
        // Fallback to local WebSocket if no server config
        const fallbackWsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
        console.log('Using fallback WebSocket URL:', fallbackWsUrl);
        setWsUrl(fallbackWsUrl);
      }
    } catch (error) {
      console.error('Failed to load configurations:', error);
      setError('Failed to load analysis configurations');
      
      // Fallback to local WebSocket on error
      const fallbackWsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      console.log('Using fallback WebSocket URL due to error:', fallbackWsUrl);
      setWsUrl(fallbackWsUrl);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('video/')) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please select a valid video file');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith('video/')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid video file');
    }
  };

  const handleConfigChange = (type, configName) => {
    setSelectedConfig(configName);
    if (configs && configs[type] && configs[type][configName]) {
      if (type === 'preprocessing') {
        setPreprocessingConfig(configs[type][configName]);
      } else if (type === 'smoothing') {
        setSmoothingConfig(configs[type][configName]);
      }
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const submitAnalysis = async () => {
    if (!file) {
      setError('Please select a video file first');
      return;
    }

    setLoading(true);
    setError(null);
    setJobStatus(null);
    setUploadProgress(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('preprocessing', JSON.stringify(preprocessingConfig));
    formData.append('smoothing', JSON.stringify(smoothingConfig));
    formData.append('pose', JSON.stringify(poseConfig));
    formData.append('performance', JSON.stringify(performanceConfig));
    formData.append('save_json', 'true');
    formData.append('swim_type', swimType);

    try {
      console.log('Submitting video for analysis...');
      console.log('File:', file.name, 'Size:', file.size);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }
      
      const response = await axios.post(`${API_BASE_URL}/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-File-Name': file.name, // Add file name for upload tracking
        },
        timeout: 300000, // 5 minutes timeout for large video processing
      });

      console.log('Analysis submitted successfully:', response.data);
      console.log('Setting job ID:', response.data.job_id);
      console.log('Setting job status:', response.data);
      setJobId(response.data.job_id);
      setJobStatus(response.data); // Use the full response data which includes progress information
    } catch (error) {
      console.error('Failed to submit analysis:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      setError(`Failed to submit video for analysis: ${error.message}`);
      setUploadProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadResult = async (type) => {
    if (!jobStatus || jobStatus.status !== 'completed') {
      console.error('No job status available for download');
      return;
    }

    if (!jobId) {
      console.error('No job ID available for download');
      setError('Cannot download: Job ID is missing');
      return;
    }

    console.log('Downloading result:', { type, jobId, jobStatus });

    try {
      const response = await axios.get(`${API_BASE_URL}/download/${jobId}?file_type=${type}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `swimsense_${type}_${jobId}.${type === 'video' ? 'mp4' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download result');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'processing':
        return '⏳';
      case 'error':
        return '❌';
      default:
        return '📊';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <nav className="header-nav">
          <div className="header-brand">
            <div className="header-logo">
              <span style={{ fontSize: '20px' }}>🏊‍♂️</span>
            </div>
            <span className="header-title">SwimSense AI</span>
          </div>
          <div className="header-nav-links">
            <a href="/" className="header-nav-link">Home</a>
            <a href="#features" className="header-nav-link">Features</a>
            <a href="#about" className="header-nav-link">About</a>
          </div>
          <div>
            <a href="/" className="header-cta">Back to Home</a>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="text-center mb-8" style={{ paddingTop: '20px', paddingBottom: '40px' }}>
        <div className="floating-animation mb-6">
          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto">
            <span className="text-4xl">🤖</span>
          </div>
        </div>
        <h1 className="text-5xl font-bold mb-4 text-white">
          AI-Powered Swim Analysis
        </h1>
        <p className="text-xl text-white max-w-2xl mx-auto mb-8">
          Upload your swim videos and get professional-level coaching insights powered by advanced AI technology.
        </p>
        <div className="flex justify-center items-center space-x-6 text-sm text-blue-200">
          <div className="flex items-center">
            <span className="text-green-300 mr-2">✓</span>
            <span>Advanced AI Analysis</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-300 mr-2">✓</span>
            <span>Real-time Processing</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-300 mr-2">✓</span>
            <span>Professional Insights</span>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="grid">
          {/* Enhanced File Upload Section */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-xl text-blue-600">📁</span>
              </div>
              <h2 className="text-2xl font-semibold">Upload Your Swim Video</h2>
            </div>
            <div
              className="dropzone"
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => document.getElementById('file-input').click()}
            >
              {file ? (
                <div className="space-y-2">
                  <div className="text-2xl mb-2">✅</div>
                  <p className="font-semibold text-lg">{file.name}</p>
                  <p className="text-gray-600">Size: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p className="text-sm text-gray-400 mt-2">Click to change file</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-4xl mb-3">📹</div>
                  <p className="text-lg font-semibold">Drop your swim video here or click to select</p>
                  <p className="text-gray-600">Supports: MP4, AVI, MOV, MKV</p>
                  <p className="text-sm text-gray-400">Maximum file size: 100MB</p>
                </div>
              )}
            </div>
            <input
              id="file-input"
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* Enhanced Configuration Section */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-xl text-purple-600">⚙️</span>
                </div>
                <h2 className="text-2xl font-semibold">Analysis Configuration</h2>
              </div>
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                {showAdvancedOptions ? 'Hide' : 'Show'} Advanced Options
              </button>
            </div>
            
            {/* Swim Type Selector — always visible */}
            <div className="form-group">
              <label className="form-label">Swim Type</label>
              <div className="swim-type-selector">
                {[
                  { value: 'race',    label: 'Race',    desc: 'Competition or time-trial — elite technique targets' },
                  { value: 'workout', label: 'Workout', desc: 'Training session — balanced technique and efficiency' },
                  { value: 'casual',  label: 'Casual',  desc: 'Recreational swimming — form and comfort focus' },
                ].map(({ value, label, desc }) => (
                  <button
                    key={value}
                    type="button"
                    className={`swim-type-btn${swimType === value ? ' active' : ''}`}
                    onClick={() => setSwimType(value)}
                    title={desc}
                  >
                    <span className="swim-type-label">{label}</span>
                    <span className="swim-type-desc">{desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {configs && configs.performance && configs.preprocessing && (
              <div className="space-y-6">
                {/* Performance Configuration */}
                <div className="form-group">
                  <label className="form-label">Performance Profile</label>
                  <select 
                    className="form-control"
                    value={performanceConfig.profile}
                    onChange={(e) => setPerformanceConfig({...performanceConfig, profile: e.target.value})}
                  >
                    {configs.performance && Object.keys(configs.performance).map(profile => (
                      <option key={profile} value={profile}>
                        {profile.charAt(0).toUpperCase() + profile.slice(1).replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-400 mt-2">
                    {configs.performance && configs.performance[performanceConfig.profile]}
                  </p>
                </div>

                {/* Preprocessing Configuration */}
                <div className="form-group">
                  <label className="form-label">Preprocessing Preset</label>
                  <select 
                    className="form-control"
                    value={selectedConfig}
                    onChange={(e) => handleConfigChange('preprocessing', e.target.value)}
                  >
                    {configs.preprocessing && Object.keys(configs.preprocessing).map(config => (
                      <option key={config} value={config}>
                        {config.charAt(0).toUpperCase() + config.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-400 mt-2">
                    Choose the preprocessing configuration for optimal video analysis
                  </p>
                </div>

                {/* Smoothing Configuration */}
                <div className="form-group">
                  <label className="form-label">Smoothing Method</label>
                  <select 
                    className="form-control"
                    value={smoothingConfig.method}
                    onChange={(e) => setSmoothingConfig({...smoothingConfig, method: e.target.value})}
                  >
                    <option value="moving_average">Moving Average</option>
                    <option value="exponential">Exponential Smoothing</option>
                    <option value="savitzky_golay">Savitzky-Golay Filter</option>
                    <option value="kalman">Kalman Filter</option>
                  </select>
                  <p className="text-sm text-gray-400 mt-2">
                    Select the smoothing algorithm for trajectory analysis
                  </p>
                </div>

                {/* Advanced Options */}
                {showAdvancedOptions && (
                  <div className="space-y-6 border-t pt-6">
                    <h3 className="text-lg font-semibold">Advanced Configuration</h3>
                    
                    {/* Performance Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label">GPU Acceleration</label>
                        <select 
                          className="form-control"
                          value={performanceConfig.enable_gpu}
                          onChange={(e) => setPerformanceConfig({...performanceConfig, enable_gpu: e.target.value === 'true'})}
                        >
                          <option value={true}>Enabled</option>
                          <option value={false}>Disabled</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Parallel Processing</label>
                        <select 
                          className="form-control"
                          value={performanceConfig.enable_parallel}
                          onChange={(e) => setPerformanceConfig({...performanceConfig, enable_parallel: e.target.value === 'true'})}
                        >
                          <option value={true}>Enabled</option>
                          <option value={false}>Disabled</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Batch Size</label>
                        <input
                          type="number"
                          className="form-control"
                          value={performanceConfig.batch_size}
                          onChange={(e) => setPerformanceConfig({...performanceConfig, batch_size: parseInt(e.target.value)})}
                          min="1"
                          max="8"
                        />
                      </div>
                    </div>

                                         {/* Pose Detection Options */}
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                       <div className="form-group">
                         <label className="form-label">Detection Confidence</label>
                         <input
                           type="range"
                           className="form-control"
                           value={poseConfig.min_detection_confidence}
                           onChange={(e) => setPoseConfig({...poseConfig, min_detection_confidence: parseFloat(e.target.value)})}
                           min="0.1"
                           max="1.0"
                           step="0.1"
                         />
                         <span className="text-sm text-gray-600">{poseConfig.min_detection_confidence}</span>
                         <p className="text-xs text-gray-400 mt-1">
                           Lower values = Higher detection rate, Lower accuracy
                         </p>
                       </div>
                       <div className="form-group">
                         <label className="form-label">Tracking Confidence</label>
                         <input
                           type="range"
                           className="form-control"
                           value={poseConfig.min_tracking_confidence}
                           onChange={(e) => setPoseConfig({...poseConfig, min_tracking_confidence: parseFloat(e.target.value)})}
                           min="0.1"
                           max="1.0"
                           step="0.1"
                         />
                         <span className="text-sm text-gray-600">{poseConfig.min_tracking_confidence}</span>
                         <p className="text-xs text-gray-400 mt-1">
                           Lower values = Better tracking, More noise
                         </p>
                       </div>
                       <div className="form-group">
                         <label className="form-label">Model Complexity</label>
                         <select 
                           className="form-control"
                           value={poseConfig.model_complexity}
                           onChange={(e) => setPoseConfig({...poseConfig, model_complexity: parseInt(e.target.value)})}
                         >
                           <option value={0}>Fast (0)</option>
                           <option value={1}>Balanced (1)</option>
                           <option value={2}>Accurate (2)</option>
                         </select>
                         <p className="text-xs text-gray-400 mt-1">
                           Higher complexity = Better accuracy, Slower processing
                         </p>
                       </div>
                     </div>
                     
                     {/* Pose Detection Rate Indicator */}
                     <div className="bg-blue-50 rounded-lg p-4">
                       <h4 className="text-md font-semibold mb-3 flex items-center">
                         <span className="text-xl mr-2">🎯</span>
                         Expected Pose Detection Rate
                       </h4>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                           <p className="text-sm text-gray-600 mb-2">Based on your current settings:</p>
                           <div className="space-y-2">
                             <div className="flex justify-between text-sm">
                               <span>Detection Confidence:</span>
                               <span className={`font-semibold ${poseConfig.min_detection_confidence <= 0.3 ? 'text-green-600' : poseConfig.min_detection_confidence <= 0.6 ? 'text-yellow-600' : 'text-red-600'}`}>
                                 {poseConfig.min_detection_confidence <= 0.3 ? 'High Rate' : poseConfig.min_detection_confidence <= 0.6 ? 'Medium Rate' : 'Low Rate'}
                               </span>
                             </div>
                             <div className="flex justify-between text-sm">
                               <span>Tracking Confidence:</span>
                               <span className={`font-semibold ${poseConfig.min_tracking_confidence <= 0.3 ? 'text-green-600' : poseConfig.min_tracking_confidence <= 0.6 ? 'text-yellow-600' : 'text-red-600'}`}>
                                 {poseConfig.min_tracking_confidence <= 0.3 ? 'High Rate' : poseConfig.min_tracking_confidence <= 0.6 ? 'Medium Rate' : 'Low Rate'}
                               </span>
                             </div>
                             <div className="flex justify-between text-sm">
                               <span>Model Complexity:</span>
                               <span className={`font-semibold ${poseConfig.model_complexity === 0 ? 'text-green-600' : poseConfig.model_complexity === 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                                 {poseConfig.model_complexity === 0 ? 'Fast' : poseConfig.model_complexity === 1 ? 'Balanced' : 'Accurate'}
                               </span>
                             </div>
                           </div>
                         </div>
                         <div>
                           <p className="text-sm text-gray-600 mb-2">Estimated Detection Rate:</p>
                           <div className="text-center">
                             <div className={`text-2xl font-bold ${
                               (poseConfig.min_detection_confidence <= 0.3 && poseConfig.min_tracking_confidence <= 0.3) ? 'text-green-600' :
                               (poseConfig.min_detection_confidence <= 0.6 && poseConfig.min_tracking_confidence <= 0.6) ? 'text-yellow-600' : 'text-red-600'
                             }`}>
                               {poseConfig.min_detection_confidence <= 0.3 && poseConfig.min_tracking_confidence <= 0.3 ? '85-95%' :
                                poseConfig.min_detection_confidence <= 0.6 && poseConfig.min_tracking_confidence <= 0.6 ? '70-85%' : '50-70%'}
                             </div>
                             <p className="text-xs text-gray-500 mt-1">
                               {poseConfig.min_detection_confidence <= 0.3 && poseConfig.min_tracking_confidence <= 0.3 ? 'Excellent detection rate' :
                                poseConfig.min_detection_confidence <= 0.6 && poseConfig.min_tracking_confidence <= 0.6 ? 'Good detection rate' : 'Lower detection rate'}
                             </p>
                           </div>
                         </div>
                       </div>
                     </div>

                    {/* Smoothing Parameters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label">Window Size</label>
                        <input
                          type="number"
                          className="form-control"
                          value={smoothingConfig.window_size}
                          onChange={(e) => setSmoothingConfig({...smoothingConfig, window_size: parseInt(e.target.value)})}
                          min="3"
                          max="15"
                        />
                      </div>
                      {smoothingConfig.method === 'exponential' && (
                        <div className="form-group">
                          <label className="form-label">Exponential Alpha</label>
                          <input
                            type="range"
                            className="form-control"
                            value={smoothingConfig.exponential_alpha}
                            onChange={(e) => setSmoothingConfig({...smoothingConfig, exponential_alpha: parseFloat(e.target.value)})}
                            min="0.1"
                            max="0.9"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">{smoothingConfig.exponential_alpha}</span>
                        </div>
                      )}
                      {smoothingConfig.method === 'savitzky_golay' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Window Length</label>
                            <input
                              type="number"
                              className="form-control"
                              value={smoothingConfig.savgol_window_length}
                              onChange={(e) => setSmoothingConfig({...smoothingConfig, savgol_window_length: parseInt(e.target.value)})}
                              min="3"
                              max="15"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Polynomial Order</label>
                            <input
                              type="number"
                              className="form-control"
                              value={smoothingConfig.savgol_polyorder}
                              onChange={(e) => setSmoothingConfig({...smoothingConfig, savgol_polyorder: parseInt(e.target.value)})}
                              min="1"
                              max="5"
                            />
                          </div>
                        </>
                      )}
                      {smoothingConfig.method === 'kalman' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Process Noise</label>
                            <input
                              type="number"
                              className="form-control"
                              value={smoothingConfig.kalman_process_noise}
                              onChange={(e) => setSmoothingConfig({...smoothingConfig, kalman_process_noise: parseFloat(e.target.value)})}
                              min="1e-5"
                              max="1e-1"
                              step="1e-5"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Measurement Noise</label>
                            <input
                              type="number"
                              className="form-control"
                              value={smoothingConfig.kalman_measurement_noise}
                              onChange={(e) => setSmoothingConfig({...smoothingConfig, kalman_measurement_noise: parseFloat(e.target.value)})}
                              min="1e-3"
                              max="1"
                              step="1e-3"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Advanced Preprocessing Options */}
                    <div className="space-y-4">
                      <h4 className="text-md font-semibold">Preprocessing Options</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-group">
                          <label className="form-label">Resize to 720p</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.enable_resize_to_720p}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, enable_resize_to_720p: e.target.value === 'true'})}
                          >
                            <option value={true}>Enabled</option>
                            <option value={false}>Disabled</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Contrast Enhancement</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.enable_contrast_enhancement}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, enable_contrast_enhancement: e.target.value === 'true'})}
                          >
                            <option value={true}>Enabled</option>
                            <option value={false}>Disabled</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Noise Reduction</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.enable_noise_reduction}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, enable_noise_reduction: e.target.value === 'true'})}
                          >
                            <option value={true}>Enabled</option>
                            <option value={false}>Disabled</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Color Correction</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.enable_color_correction}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, enable_color_correction: e.target.value === 'true'})}
                          >
                            <option value={true}>Enabled</option>
                            <option value={false}>Disabled</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Visibility Enhancement</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.enable_visibility_enhancement}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, enable_visibility_enhancement: e.target.value === 'true'})}
                          >
                            <option value={true}>Enabled</option>
                            <option value={false}>Disabled</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Noise Reduction Method</label>
                          <select 
                            className="form-control"
                            value={preprocessingConfig.noise_reduction_method}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, noise_reduction_method: e.target.value})}
                          >
                            <option value="gaussian">Gaussian Blur</option>
                            <option value="bilateral">Bilateral Filter</option>
                          </select>
                        </div>
                      </div>

                      {/* Advanced Preprocessing Parameters */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-group">
                          <label className="form-label">CLAHE Clip Limit</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.clahe_clip_limit}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, clahe_clip_limit: parseFloat(e.target.value)})}
                            min="1.0"
                            max="5.0"
                            step="0.5"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.clahe_clip_limit}</span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Blur Strength</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.blur_strength}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, blur_strength: parseFloat(e.target.value)})}
                            min="0.1"
                            max="2.0"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.blur_strength}</span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Saturation Factor</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.saturation_factor}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, saturation_factor: parseFloat(e.target.value)})}
                            min="0.5"
                            max="2.0"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.saturation_factor}</span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Blue Factor</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.blue_factor}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, blue_factor: parseFloat(e.target.value)})}
                            min="0.5"
                            max="1.5"
                            step="0.01"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.blue_factor}</span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Red Factor</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.red_factor}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, red_factor: parseFloat(e.target.value)})}
                            min="0.5"
                            max="1.5"
                            step="0.01"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.red_factor}</span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Brightness Offset</label>
                          <input
                            type="range"
                            className="form-control"
                            value={preprocessingConfig.brightness_offset}
                            onChange={(e) => setPreprocessingConfig({...preprocessingConfig, brightness_offset: parseInt(e.target.value)})}
                            min="-50"
                            max="50"
                            step="5"
                          />
                          <span className="text-sm text-gray-600">{preprocessingConfig.brightness_offset}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Submit Button */}
        <div className="card text-center">
          <button
            className="btn"
            onClick={submitAnalysis}
            disabled={!file || loading}
          >
            {loading ? (
              <span className="flex items-center">
                <div className="loading"></div>
                <span>Processing your video...</span>
              </span>
            ) : (
              <>
                <span className="text-xl">🚀</span>
                <span>Start AI Analysis</span>
              </>
            )}
          </button>
          {!loading && (
            <p className="text-sm text-gray-400 mt-3">
              Analysis typically takes 2-5 minutes depending on video length
            </p>
          )}
        </div>

        {/* Upload Progress Display */}
        {uploadProgress && (
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-xl text-blue-600">📤</span>
              </div>
              <h2 className="text-2xl font-semibold">Upload Progress</h2>
            </div>
            
            <div className="space-y-4">
              {/* Upload Status */}
              <div className={`status ${uploadProgress.type === 'upload_error' ? 'error' : 'info'}`}>
                <span className="text-xl">
                  {uploadProgress.type === 'upload_start' && '📤'}
                  {uploadProgress.type === 'upload_progress' && '📤'}
                  {uploadProgress.type === 'upload_complete' && '✅'}
                  {uploadProgress.type === 'upload_processing' && '⚙️'}
                  {uploadProgress.type === 'analysis_started' && '🚀'}
                  {uploadProgress.type === 'upload_error' && '❌'}
                </span>
                <div>
                  <strong>Status:</strong> {uploadProgress.message}
                  {uploadProgress.error && (
                    <p className="mt-1 text-sm text-red-600">{uploadProgress.error}</p>
                  )}
                </div>
              </div>

              {/* File Information */}
              {uploadProgress.fileName && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium">File:</span>
                    <span className="text-gray-600">{uploadProgress.fileName}</span>
                  </div>
                  {uploadProgress.totalSize && (
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="font-medium">Size:</span>
                      <span className="text-gray-600">{formatFileSize(uploadProgress.totalSize)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Upload Progress Bar */}
              {uploadProgress.progress !== undefined && uploadProgress.progress !== null && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Upload Progress</span>
                    <span>{uploadProgress.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        uploadProgress.type === 'upload_error' ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${uploadProgress.progress}%` }}
                    ></div>
                  </div>
                  {uploadProgress.uploadedBytes && uploadProgress.totalSize && (
                    <div className="text-xs text-gray-500 text-center">
                      {formatFileSize(uploadProgress.uploadedBytes)} / {formatFileSize(uploadProgress.totalSize)}
                    </div>
                  )}
                </div>
              )}

              {/* Processing Status */}
              {uploadProgress.type === 'upload_processing' && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <div className="loading mr-2"></div>
                    <span className="text-blue-700">Processing uploaded file...</span>
                  </div>
                </div>
              )}

              {/* Analysis Started */}
              {uploadProgress.type === 'analysis_started' && (
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    <span className="text-green-700">Upload completed! Analysis started successfully.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* WebSocket Connection Status */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm">
                {isConnected ? 'Real-time updates connected' : 'Real-time updates disconnected'}
              </span>
            </div>
            {!isConnected && (
              <span className="text-xs text-gray-500">
                Falling back to manual refresh
              </span>
            )}
          </div>
        </div>

        {/* Enhanced Status and Results */}
        {error && (
          <div className="card">
            <div className={`status ${getStatusClass('error')}`}>
              <span className="text-xl">❌</span>
              <div>
                <strong>Error:</strong> {error}
              </div>
            </div>
          </div>
        )}

        {jobStatus && (
          <div className="card" id="analysis-status">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-xl text-green-600">📊</span>
                </div>
                <h2 className="text-2xl font-semibold">Analysis Status</h2>
              </div>
              {!isConnected && jobId && (
                <button
                  onClick={checkJobStatus}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  🔄 Refresh Status
                </button>
              )}
            </div>
            
            <div className={`status ${getStatusClass(jobStatus.status)}`}>
              <span className="text-xl">{getStatusIcon(jobStatus.status)}</span>
              <div>
                <strong>Status:</strong> {jobStatus.status.charAt(0).toUpperCase() + jobStatus.status.slice(1)}
                {jobStatus.message && <p className="mt-1 text-sm">{jobStatus.message}</p>}
              </div>
            </div>

            {/* Progress Steps */}
            {jobStatus.progress && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="text-xl mr-2">📋</span>
                  Processing Steps
                </h3>
                <div className="space-y-3">
                  {jobStatus.progress.steps.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        step.status === 'completed' ? 'bg-green-500 text-white' :
                        step.status === 'in_progress' ? 'bg-blue-500 text-white animate-pulse' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {step.status === 'completed' ? '✓' :
                         step.status === 'in_progress' ? '⟳' : (index + 1)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{step.name}</div>
                        {step.status === 'in_progress' && jobStatus.progress.current_step === step.id && (
                          <div className="text-sm text-blue-600 mt-1">
                            {jobStatus.message}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Step {jobStatus.progress.step_number} of {jobStatus.progress.total_steps}</span>
                    <span>{Math.round((jobStatus.progress.step_number / jobStatus.progress.total_steps) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(jobStatus.progress.step_number / jobStatus.progress.total_steps) * 100}%` }}
                    ></div>
                  </div>
                  
                  {/* Frame Progress Details */}
                  {jobStatus.progress.frame_progress && (
                    <div className="mt-4 space-y-3">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Frame Progress</span>
                        <span>{jobStatus.progress.frame_progress.current_frame} / {jobStatus.progress.frame_progress.total_frames}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${jobStatus.progress.frame_progress.percentage}%` }}
                        ></div>
                      </div>
                      
                      {/* Real-time Performance Metrics */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {jobStatus.progress.current_fps && (
                          <div className="bg-blue-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Current FPS</span>
                              <span className="font-semibold text-blue-600">{jobStatus.progress.current_fps.toFixed(1)}</span>
                            </div>
                          </div>
                        )}
                        {jobStatus.progress.frame_progress.percentage && (
                          <div className="bg-green-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-semibold text-green-600">{jobStatus.progress.frame_progress.percentage.toFixed(1)}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {jobStatus.status === 'completed' && (
              <div className="mt-6 space-y-4" id="analysis-results">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="text-2xl mr-2">✅</span>
                    Analysis Complete!
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <button 
                      className="btn btn-success"
                      onClick={() => downloadResult('video')}
                    >
                      <span>📹</span>
                      <span>Download Video</span>
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => downloadResult('json')}
                    >
                      <span>📄</span>
                      <span>Download Data</span>
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setComparisonMode('original');
                        setShowVideoComparison(true);
                      }}
                    >
                      <span>🔄</span>
                      <span>Compare Videos (720p)</span>
                    </button>
                    <button
                      className="btn btn-ideal"
                      onClick={() => {
                        setComparisonMode('reference');
                        setShowVideoComparison(true);
                      }}
                    >
                      <span>🏆</span>
                      <span>Compare to Ideal</span>
                    </button>
                  </div>
                </div>

                {/* Stroke Analysis Component */}
                <StrokeAnalysis 
                  analysisData={jobStatus.analysis_data}
                  jobId={jobId}
                />

                {jobStatus.analysis_data && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold mb-4 flex items-center">
                      <span className="text-xl mr-2">📈</span>
                      Analysis Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-400">Total Frames</p>
                        <p className="text-2xl font-bold text-blue-600">{jobStatus.analysis_data.total_frames}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-400">Smoothing Method</p>
                        <p className="text-lg font-semibold">{jobStatus.analysis_data.smoothing_method.replace('_', ' ')}</p>
                      </div>
                      {jobStatus.analysis_data.original_dimensions && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-400">Original Resolution</p>
                          <p className="text-lg font-semibold">
                            {jobStatus.analysis_data.original_dimensions.width} × {jobStatus.analysis_data.original_dimensions.height}
                          </p>
                        </div>
                      )}
                      {jobStatus.analysis_data.output_dimensions && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-400">Output Resolution</p>
                          <p className="text-lg font-semibold">
                            {jobStatus.analysis_data.output_dimensions.width} × {jobStatus.analysis_data.output_dimensions.height}
                            {jobStatus.analysis_data.resized_to_720p && (
                              <span className="text-sm text-green-600 ml-2">(720p)</span>
                            )}
                          </p>
                        </div>
                      )}
                      {jobStatus.analysis_data.optimization_enabled && (
                        <>
                          <div className="bg-white rounded-lg p-4">
                            <p className="text-sm text-gray-400">GPU Acceleration</p>
                            <p className="text-lg font-semibold">
                              {jobStatus.analysis_data.optimization_enabled.gpu_acceleration ? '✅ Enabled' : '❌ Disabled'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4">
                            <p className="text-sm text-gray-400">Parallel Processing</p>
                            <p className="text-lg font-semibold">
                              {jobStatus.analysis_data.optimization_enabled.parallel_processing ? '✅ Enabled' : '❌ Disabled'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {jobStatus.analysis_data.average_metrics && (
                      <div className="mt-4">
                        <h5 className="font-semibold mb-3">Average Metrics</h5>
                        <div className="bg-white rounded-lg p-4 overflow-x-auto">
                          <pre className="text-sm">{JSON.stringify(jobStatus.analysis_data.average_metrics, null, 2)}</pre>
                        </div>
                      </div>
                    )}

                                         {jobStatus.analysis_data.performance_stats && (
                       <div className="mt-4">
                         <h5 className="font-semibold mb-3">Performance Statistics</h5>
                         <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                           <div className="bg-white rounded-lg p-4">
                             <p className="text-sm text-gray-400">Average FPS</p>
                             <p className="text-xl font-bold text-green-600">
                               {jobStatus.analysis_data.performance_stats.fps?.toFixed(1) || 'N/A'}
                             </p>
                           </div>
                           <div className="bg-white rounded-lg p-4">
                             <p className="text-sm text-gray-400">Avg Frame Time</p>
                             <p className="text-lg font-semibold">
                               {(jobStatus.analysis_data.performance_stats.avg_frame_time * 1000)?.toFixed(1) || 'N/A'} ms
                             </p>
                           </div>
                           <div className="bg-white rounded-lg p-4">
                             <p className="text-sm text-gray-400">Memory Usage</p>
                             <p className="text-lg font-semibold">
                               {jobStatus.analysis_data.performance_stats.avg_memory_usage?.toFixed(1) || 'N/A'}%
                             </p>
                           </div>
                           <div className="bg-white rounded-lg p-4">
                             <p className="text-sm text-gray-400">Pose Detection Rate</p>
                             <p className="text-xl font-bold text-blue-600">
                               {jobStatus.analysis_data.performance_stats.pose_detection_rate?.toFixed(1) || 'N/A'}%
                             </p>
                             <p className="text-xs text-gray-500 mt-1">
                               {jobStatus.analysis_data.performance_stats.pose_detection_success_count || 0} / {jobStatus.analysis_data.performance_stats.total_frames || 0} frames
                             </p>
                           </div>
                         </div>
                         
                         {/* Pose Detection Details */}
                         {jobStatus.analysis_data.performance_stats.pose_detection_rate !== undefined && (
                           <div className="mt-4">
                             <h6 className="font-semibold mb-2 text-sm text-gray-600">Pose Detection Details</h6>
                             <div className="bg-white rounded-lg p-4">
                               <div className="flex items-center justify-between mb-2">
                                 <span className="text-sm text-gray-600">Detection Success Rate</span>
                                 <span className="text-sm font-semibold">
                                   {jobStatus.analysis_data.performance_stats.pose_detection_rate?.toFixed(1)}%
                                 </span>
                               </div>
                               <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                 <div 
                                   className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                   style={{ width: `${jobStatus.analysis_data.performance_stats.pose_detection_rate || 0}%` }}
                                 ></div>
                               </div>
                               <div className="grid grid-cols-2 gap-4 text-sm">
                                 <div className="flex justify-between">
                                   <span className="text-gray-600">Successful Detections:</span>
                                   <span className="font-semibold text-green-600">
                                     {jobStatus.analysis_data.performance_stats.pose_detection_success_count || 0}
                                   </span>
                                 </div>
                                 <div className="flex justify-between">
                                   <span className="text-gray-600">Failed Detections:</span>
                                   <span className="font-semibold text-red-600">
                                     {jobStatus.analysis_data.performance_stats.pose_detection_failure_count || 0}
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>
                         )}
                       </div>
                     )}
                  </div>
                )}
              </div>
            )}

            {jobStatus.status === 'error' && (
              <div className="status error">
                <span className="text-xl">❌</span>
                <div>
                  <strong>Analysis Failed:</strong> {jobStatus.message}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Video Comparison Modal */}
        {showVideoComparison && jobStatus && jobStatus.status === 'completed' && (
          <VideoComparison
            jobId={jobId}
            originalFilename={file?.name}
            onClose={() => setShowVideoComparison(false)}
            comparisonMode={comparisonMode}
          />
        )}
      </div>
    </div>
  );
}

export default App; 